import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, FileText } from 'lucide-react';
import classNames from 'classnames';

export type QuestDecision = 'goFill' | 'continue' | 'cancel';

type Props = {
  open: boolean;
  onClose: (decision: QuestDecision) => void;
  // i18n 名前空間キー。デフォルトは 'mypage'
  i18nNsKey?: string;
};

const QuestionnaireGuardDialog: React.FC<Props> = ({
  open,
  onClose,
  i18nNsKey = 'mypage',
}) => {
  const { t } = useTranslation();
  const [riskAccepted, setRiskAccepted] = useState(false);

  if (!open) return null;

  const k = (suffix: string) => `${i18nNsKey}.${suffix}`;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      {/* 背景クリックでキャンセル */}
      <button
        type="button"
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={() => onClose('cancel')}
        aria-label="Close"
      />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-[92%] p-7 animate-in zoom-in-95 duration-200">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
             <FileText className="w-5 h-5 text-blue-500" />
          </div>
          <h3 className="text-lg font-bold text-gray-900">
            {t(k('quest_guard_title'))}
          </h3>
        </div>

        <p className="text-[14px] text-gray-600 mb-5 leading-relaxed">
          {t(k('quest_guard_desc'))}
        </p>

        <div className="mb-6 p-4 bg-orange-50 border border-orange-100 rounded-xl">
          <div className="flex items-start gap-2.5">
            <AlertCircle className="w-4 h-4 text-orange-600 mt-0.5 shrink-0" />
            <p className="text-[13px] text-orange-700 font-bold leading-relaxed">
              {t(k('quest_guard_risk_high'))}
            </p>
          </div>
        </div>

        <div className="mb-6 px-1">
          <label className="flex items-start gap-3 cursor-pointer group">
            <div className="relative flex items-center mt-0.5">
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500 cursor-pointer transition-all"
                checked={riskAccepted}
                onChange={(e) => setRiskAccepted(e.target.checked)}
              />
            </div>
            <span className="text-[12px] text-gray-700 leading-relaxed font-medium group-hover:text-gray-900 transition-colors">
              {t(k('quest_guard_risk_checkbox'))}
            </span>
          </label>
        </div>

        <div className="space-y-3">
          <button
            className="w-full h-12 flex items-center justify-center rounded-xl bg-orange-500 text-white font-bold shadow-md hover:bg-orange-600 transition-all hover:-translate-y-0.5 active:scale-[0.98]"
            onClick={() => onClose('goFill')}
          >
            {t(k('quest_guard_go_fill'))}
          </button>

          <button
            className={classNames(
              "w-full h-11 flex items-center justify-center rounded-xl border font-medium transition-all active:scale-[0.98]",
              !riskAccepted
                ? "bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed"
                : "border-gray-200 text-gray-700 hover:bg-gray-50 bg-white shadow-sm"
            )}
            onClick={() => {
              if (!riskAccepted) return;
              onClose('continue');
            }}
            disabled={!riskAccepted}
          >
            {t(k('quest_guard_continue'))}
          </button>

          <button
            className="w-full pt-1 text-xs text-gray-400 hover:text-gray-600 transition-colors text-center"
            onClick={() => onClose('cancel')}
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionnaireGuardDialog;
